import {
    selectCurrentDeviceKit,
    selectIsDeviceConnectionEnd,
    selectIsDeviceConnectIsFailed,
    selectIsDeviceSyncIsFailed,
} from '../../state/selectors/iotDevices';
import {getState} from '../../state/store';
import notificationService from '../notificationService';

/**
 * Device Kit Notification Service
 *
 * Shows failure notifications after connection or sync ends for a kit member.
 * For full kits (charger + holder), defers until both devices finish
 * to avoid showing a warning while the second device is still in progress.
 *
 */

// Accumulates DUSNs of kit members that finished sync; cleared once both are done
const completedSyncDevices = new Set();

const getKitMembers = () => {
    const {charger, holder} = selectCurrentDeviceKit(getState());

    return {chargerDusn: charger?.serialNumber, holderDusn: holder?.serialNumber};
};

const isKitMember = (dusn, chargerDusn, holderDusn) => dusn === chargerDusn || dusn === holderDusn;

const notifyKitSyncResult = (dusn) => {
    const {chargerDusn, holderDusn} = getKitMembers();

    // Ignore if the device isn't part of the current kit
    if (!isKitMember(dusn, chargerDusn, holderDusn)) return;

    const isFullKit = chargerDusn && holderDusn;

    // If not a full kit, show warning immediately for the single device
    if (!isFullKit) return showSyncFailureWarning(chargerDusn, holderDusn);

    completedSyncDevices.add(dusn);

    const isBothCompleted = completedSyncDevices.has(chargerDusn) && completedSyncDevices.has(holderDusn);

    // If full kit, wait until both devices have completed sync before showing any warnings
    if (!isBothCompleted) return;

    showSyncFailureWarning(chargerDusn, holderDusn);

    completedSyncDevices.clear();
};

const showSyncFailureWarning = (chargerDusn, holderDusn) => {
    const state = getState();
    const isChargerFailed = selectIsDeviceSyncIsFailed(chargerDusn)(state);
    const isHolderFailed = selectIsDeviceSyncIsFailed(holderDusn)(state);

    // If both devices failed, show a single warning for the kit; otherwise show individual warnings
    if (isChargerFailed && isHolderFailed) return notificationService.showRefreshDeviceKitSettingsWarning(chargerDusn);
    if (isHolderFailed) return notificationService.showRefreshDeviceSettingsWarning(holderDusn);
    if (isChargerFailed) return notificationService.showRefreshDeviceSettingsWarning(chargerDusn);
};

const notifyKitConnectionResult = (dusn) => {
    const {chargerDusn, holderDusn} = getKitMembers();

    if (!isKitMember(dusn, chargerDusn, holderDusn)) return;

    const state = getState();
    const isChargerDone = selectIsDeviceConnectionEnd(chargerDusn)(state);
    const isHolderDone = selectIsDeviceConnectionEnd(holderDusn)(state);

    // Wait until both devices have finished the connection process before showing any warnings
    if (!isChargerDone || !isHolderDone) return;

    showConnectionFailureWarning(chargerDusn, holderDusn);
};

const showConnectionFailureWarning = (chargerDusn, holderDusn) => {
    const state = getState();
    const isChargerFailed = selectIsDeviceConnectIsFailed(chargerDusn)(state);
    const isHolderFailed = selectIsDeviceConnectIsFailed(holderDusn)(state);

    // If both devices failed, show a single warning for the kit; otherwise show individual warnings
    if (isChargerFailed && isHolderFailed) return notificationService.showReconnectDeviceKitWarning(chargerDusn);
    if (isHolderFailed) return notificationService.showReconnectDeviceWarning(holderDusn);
    if (isChargerFailed) return notificationService.showReconnectDeviceWarning(chargerDusn);
};

export {notifyKitConnectionResult, notifyKitSyncResult};
